import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Pause, Play } from "lucide-react";
import { PauseContext } from "./shared";
import { DesignAnim } from "./DesignAnim";
import { WifiAnim } from "./WifiAnim";
import { FirewallAnim } from "./FirewallAnim";
import { MultiSiteAnim } from "./MultiSiteAnim";
import { CctvAnim } from "./CctvAnim";
import { NocAnim } from "./NocAnim";
import { ease } from "../motion";

const ANIMS = { design: DesignAnim, wifi: WifiAnim, security: FirewallAnim, sdwan: MultiSiteAnim, cctv: CctvAnim, support: NocAnim };

export const Stage = ({ id, title, testid = "capability-stage" }) => {
  const Anim = ANIMS[id];
  const [caption, setCaption] = useState("");
  const [paused, setPaused] = useState(false);
  const [slot, setSlot] = useState(null);
  const pausedRef = useRef(false);
  const area = useRef(null);
  const setPause = (v) => {
    pausedRef.current = v;
    setPaused(v);
    area.current?.querySelectorAll("svg").forEach((s) => (v ? s.pauseAnimations?.() : s.unpauseAnimations?.()));
  };
  useEffect(() => {
    pausedRef.current = false;
    setPaused(false);
  }, [id]);

  return (
    <div className="chamfer relative border border-white/10 bg-[#080B12]" data-testid={testid}>
      <div className="flex items-center justify-between border-b border-white/10 px-4 py-2 font-mono text-[9px] tracking-[0.2em] text-slate-500">
        <span>LNS://capabilities/{id}</span>
        <div className="flex items-center gap-3">
          <span className={`flex items-center gap-1.5 ${paused ? "text-amber-400" : "text-emerald-400"}`}>
            <span className={`h-1.5 w-1.5 rounded-full ${paused ? "bg-amber-400" : "bg-emerald-400 blink"}`} />
            {paused ? "PAUSED" : "LIVE"}
          </span>
          <button type="button" onClick={() => setPause(!paused)} data-testid={`${testid}-pause-toggle`} aria-label={paused ? "Play animation" : "Pause animation"} className={`flex items-center gap-1.5 rounded-full border px-3 py-1 transition-colors ${paused ? "border-cyan-300 bg-cyan-300 text-void hover:bg-white" : "border-white/20 text-slate-200 hover:border-cyan-300 hover:text-cyan-300"}`}>
            {paused ? <Play size={11} fill="currentColor" /> : <Pause size={11} fill="currentColor" />}
            {paused ? "PLAY" : "PAUSE"}
          </button>
        </div>
      </div>
      <PauseContext.Provider value={pausedRef}>
      <div ref={area} className={`relative aspect-[3/2] grid-bg-tight ${paused ? "is-paused" : ""}`} data-testid={`${testid}-area`}>
        <AnimatePresence mode="wait">
          <motion.div key={id} className="absolute inset-0" initial={{ opacity: 0, filter: "blur(6px)" }} animate={{ opacity: 1, filter: "blur(0px)" }} exit={{ opacity: 0, filter: "blur(6px)" }} transition={{ duration: 0.45, ease }} data-testid={`capability-anim-${id}`} aria-label={title}>
            <Anim say={setCaption} slot={slot} />
          </motion.div>
        </AnimatePresence>
      </div>
      <div ref={setSlot} className="empty:hidden border-t border-white/10 px-4 py-3" />
      </PauseContext.Provider>
      <div className="flex items-start gap-3 border-t border-white/10 pl-9 pr-5 sm:pl-10 py-4 min-h-[68px]" aria-live="polite">
        <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-cyan-300 shadow-[0_0_10px_#00F0FF] blink" />
        <AnimatePresence mode="wait">
          <motion.p key={caption} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.35, ease }} className="text-sm sm:text-base text-slate-200 leading-snug" data-testid={`${testid}-caption`}>
            {caption}
          </motion.p>
        </AnimatePresence>
      </div>
    </div>
  );
};
